import { useCallback, useEffect, useState } from 'react';
import { asyncValidateEmail, validateEmail } from '../../lib/models/email';
import { requestOtpByEmail } from '../../lib/use-cases/request-otp-by-email';
import { verifyOtp } from '../../lib/use-cases/verify-otp';
import { useTranslation } from 'react-i18next';
import { useRouting } from './use-routing';
import { pagePathnames } from '../pages/page-routes';
import { useAuthentication } from './use-authentication';
import { useResendTimer, OTP_DELAY, RESEND_DELAY } from './use-resend-timer';
import { logEvent } from '../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../lib/shared/event-types';
import { isMockTestEmail, startMockTestSession } from '../../lib/shared/mock-test-user';

const STEPS = Object.freeze({
    EMAIL: 'email',
    OTP: 'otp'
});

function useAuthForm() {
    const { navigate, buildPath, lang } = useRouting();
    const { t } = useTranslation();
    const { getUserData } = useAuthentication();
    const { secondsLeft: otpCountdown, startTimer: startOtpTimer } = useResendTimer(OTP_DELAY);
    const { secondsLeft: resendCountdown, startTimer: startResendTimer } = useResendTimer(RESEND_DELAY);
    const [step, setStep] = useState(STEPS.EMAIL);
    const [isPending, setIsPending] = useState(false);
    const [isOtpPending, setIsOtpPending] = useState(false);
    const [fields, setFields] = useState({ email: '', code: '' });
    const [errors, setErrors] = useState({ email: '', form: '' });
    const [isResent, setIsResent] = useState(false);

    useEffect(() => {
        if (otpCountdown === 0) {
            setErrors(prev => ({ ...prev, form: '' }));
        }
    }, [otpCountdown]);

    const changeField = useCallback((name, value) => {
        if (errors[name] || errors.form) {
            setErrors(prevState => ({ ...prevState, [name]: '', form: '' }));
        }

        setFields(prevState => ({ ...prevState, [name]: value }));
    }, [errors]);

    const submitEmail = async (e) => {
        e?.preventDefault();

        try {
            setIsPending(true);

            const validationData = validateEmail(fields.email);

            if (!validationData.isValid) {
                logEvent(EVENT_TYPES.SIGN_IN_EMAIL_INPUT_FAILED, { code: validationData.code });

                return setErrors(prevState => ({ ...prevState, email: t(`validation.email.${validationData.code}`) }));
            }

            const asyncValidationData = await asyncValidateEmail(fields.email);

            if (!asyncValidationData.isValid) {
                logEvent(EVENT_TYPES.SIGN_IN_EMAIL_INPUT_FAILED, { code: asyncValidationData.code });

                const formError = asyncValidationData.code === 'didYouMean'
                    ? t('validation.email.didYouMean', { suggestion: asyncValidationData.data.didYouMean })
                    : t('validation.email.invalidEmail');

                return setErrors(prevState => ({ ...prevState, form: formError }));
            }

            logEvent(EVENT_TYPES.SIGN_IN_EMAIL_INPUT_SUCCESSFUL);

            if (isMockTestEmail(fields.email)) {
                startMockTestSession();
                await getUserData();
                navigate(buildPath(pagePathnames.dashboard));

                return;
            }

            await requestOtpByEmail({ email: fields.email, languageCode: lang });

            startResendTimer();
            setFields(prev => ({ ...prev, code: '' }));
            setErrors(prev => ({ ...prev, form: '' }));
            setStep(STEPS.OTP);
        } catch (error) {
            if (error?.code === 'TooManyRequests') {
                startResendTimer();

                return setErrors(prevState => ({ ...prevState, form: t('validation.form.tooManyAttempts') }));
            }

            setErrors(prevState => ({ ...prevState, form: t('validation.form.emailMayHaveMistake', { email: fields.email }) }));
        } finally {
            setIsPending(false);
        }
    };

    const resendOtp = async (e) => {
        e?.preventDefault();

        try {
            setIsPending(true);

            await requestOtpByEmail({ email: fields.email, languageCode: lang });

            startResendTimer();
            logEvent(EVENT_TYPES.SIGN_IN_OTP_RESENT);
            setIsResent(true);
            setFields(prev => ({ ...prev, code: '' }));
            setErrors(prev => ({ ...prev, form: '' }));
        } catch (error) {
            if (error?.code === 'TooManyRequests') {
                return setErrors(prevState => ({ ...prevState, form: t('validation.form.tooManyAttempts') }));
            }

            setErrors(prevState => ({ ...prevState, form: error?.message || t('validation.form.failedToSendCode') }));
        } finally {
            setIsPending(false);
        }
    };

    const submitOtp = async (code) => {
        try {
            setIsOtpPending(true);
            setFields(prev => ({ ...prev, code }));

            await verifyOtp({ email: fields.email, code });
            await getUserData();

            navigate(buildPath(pagePathnames.dashboard));
        } catch (error) {
            logEvent(EVENT_TYPES.SIGN_IN_OTP_FAILED);

            if (error?.code === 'TooManyRequests') {
                startOtpTimer();

                return;
            }

            setErrors(prevState => ({ ...prevState, form: t('validation.form.otpFailed') }));
        } finally {
            setIsOtpPending(false);
        }
    };

    const goBack = () => {
        setErrors({ email: '', form: '' });
        setIsResent(false);
        setStep(STEPS.EMAIL);
    };

    const clearFormError = () => setErrors(prev => ({ ...prev, form: '' }));

    return {
        step,
        STEPS,
        fields,
        errors,
        isPending,
        isOtpPending,
        isResent,
        resendCountdown,
        otpCountdown,
        changeField,
        submitEmail,
        resendOtp,
        submitOtp,
        goBack,
        clearFormError
    };
}

export {
    useAuthForm
};
